import React, { useState, useEffect, ReactElement, useRef, useCallback } from 'react';
import Moment from 'moment';
import { actions as projectLocationActions } from '../../../../actions/projectLocationActions';
import { actions as projectManagerActions } from '../../../../actions/projectManagerActions';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators, type Dispatch } from 'redux';
import { DataTable } from 'primereact/datatable';
import * as signalR from '@microsoft/signalr';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { Project } from '../../../../types/common';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT } from '../../../../constants/constants';
import * as types from '../../../../constants/actionTypes';
import Notification from '../../../Common/Notification';
import Modal from '../../../Common/Modal/Modal';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import Modal61 from '../../../Common/Modal/Modal61';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import { PROJECT_LOCATION_BULK_UPDATE_OPTIONS as BulkOptions } from '../../../../constants/constants';
import AddToScopeDrawer from '../../../Subscription/Admin/ProjectLocations/AddToScopeDrawer';
import ChangeProjectManagerDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeProjectManagerDrawer';
import AddBaselineDrawer from '../../../Subscription/Admin/ProjectLocations/AddBaselineDrawer';
import ChangeZeroDateDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeZeroDateDrawer/ChangeZeroDateDrawer';
import './Publish.scss';
import { InfoWindowBody, InfoWindowTopicTitle, InfoWindowTopicBody, InfoWindowIconDescription, InfoWindowIcon, SelectAllCheck } from './styles';
import { BACKEND_HOST } from '../../../../helpers/apiConfig';
import signalRService from '../../../../services/signalRService';
import { MultiSelectOption } from '../../../../types/MultiSelectOption';
import { ProjectLocation } from '../../../../types/ProjectLocation';

interface PublishProps {
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	projectData: Project;
	subscriptionId: number;
}

interface SearchParams {
	projectKey: string;
	baselineIds: Array<number>;
	isActive: boolean;
	pageSize: number;
	pageNumber: number;
	sortBy: string;
	sortDir: string;
	isOnHold: boolean | null;
	multiColumnSort: Array<{ [key: string]: string }>;
	isPushedDown: boolean | null;
	locationAbbreviations: Array<string>;
	projectManagerIds: Array<number>;
	zeroDates: Array<string>;
	projectZeroDates: Array<MultiSelectOption>;
	baselines: Array<MultiSelectOption>;
	projectManagers: Array<MultiSelectOption>;
	locationName: string;
}

const bulkOptions = [
	{ id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label },
	{ id: BulkOptions.Publish.value, name: BulkOptions.Publish.label },
	{ id: BulkOptions.RemoveScope.value, name: BulkOptions.RemoveScope.label },
	{ id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
	{ id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
	{ id: BulkOptions.Unpublish.value, name: BulkOptions.Unpublish.label, icon: BulkOptions.Unpublish.icon },
	{ id: BulkOptions.OnHold.value, name: BulkOptions.OnHold.label, icon: BulkOptions.OnHold.icon },
	{ id: BulkOptions.OffHold.value, name: BulkOptions.OffHold.label, icon: BulkOptions.OffHold.icon },
];

interface ProjectLocationFilters {
	projectZeroDates: Array<MultiSelectOption>;
	baselines: Array<MultiSelectOption>;
	projectManagers: Array<MultiSelectOption>;
	locationAbbreviations: string;
	locationName: string;
}

const connection = new signalR.HubConnectionBuilder().withUrl(`${BACKEND_HOST}/pushdownHub`).withAutomaticReconnect([0, 2000, 10000, 30000]).build();

const Publish = ({ setIsLoading, projectData, subscriptionId }: PublishProps): ReactElement => {
	const dispatch: Dispatch = useDispatch();
	const [locations, setLocations] = useState<Array<ProjectLocation>>([]);
	const [selectedLocations, setSelectedLocations] = useState<Array<ProjectLocation>>([]);
	const [zeroDate, setZeroDate] = useState<Date | null>(null);
	const [selectedBaselines, setSelectedBaselines] = useState([]);
	const [projectManagers, setProjectManagers] = useState([]);
	const [projectBaselines, setProjectBaselines] = useState([]);
	const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
	const [applicableProjectDays, setApplicableProjectDays] = useState([0, 1, 2, 3, 4, 5, 6]);
	const [selectedBulkOperation, setSelectedBulkOperation] = useState(BulkOptions.BulkUpdate.value);
	const [isAddToScopeDrawerOpen, setIsAddToScopeDrawerOpen] = useState(false);
	const [isChangeProjectManagerDrawerOpen, setIsChangeProjectManagerDrawerOpen] = useState(false);
	const [isAddBaselineDrawerOpen, setIsAddBaselineDrawerOpen] = useState(false);
	const [isChangeZeroDateDrawerOpen, setIsChangeZeroDateDrawerOpen] = useState(false);
	const [isLoadingFormFilters, setIsLoadingFormFilters] = useState(false);
	const [showRemoveScopeWarning, setShowRemoveScopeWarning] = useState(false);
	const [showRevokePushdownWarning, setShowRevokePushdownWarning] = useState(false);
	const [showOnholdWarning, setShowOnholdWarning] = useState(false);
	const [projectManagersForDrawer, setpProjectManagersForDrawer] = useState([]);
	const [availableBulkOperations, setAvailableBulkOperations] = useState(bulkOptions);
	const [isHoldOperation, setIsHoldOperation] = useState(true);
	const [isPublishing, setIsPublishing] = useState<Set<number>>(new Set());

	const [projectLocationListFilters, setProjectLocationListFilters] = useState<ProjectLocationFilters>({
		projectZeroDates: [],
		baselines: [],
		projectManagers: [],
		locationAbbreviations: '',
		locationName: '',
	});

	const [selectedProjectOwner, setSelectedProjectOwner] = useState(projectData.projectManagerId);

	const [searchParams, setSearchParams] = useState<SearchParams>({
		projectKey: projectData.projectKey,
		baselineIds: [],
		locationName: '',
		isActive: true,
		pageSize: DEFAULT_PAGE_SIZE,
		pageNumber: 1,
		sortBy: 'locationName',
		sortDir: 'asc',
		isOnHold: null,
		isPushedDown: null,
		baselines: [],
		projectZeroDates: [],
		projectManagers: [],
		locationAbbreviations: [],
		projectManagerIds: [],
		zeroDates: [],
		multiColumnSort: [
			{
				sortBy: 'locationName',
				sortDir: 'asc',
			},
		],
	});

	const [totalCount, setTotalCount] = useState<number>(0);

	const SelectAllCheckbox = () => {
		const isAllSelected = selectedLocations.length === locations.length && locations.length > 0;

		return (
			<Checkbox
				checked={isAllSelected}
				onChange={toggleSelectAll}
			/>
		);
	};

	const activeOptions = [
		{ label: 'Published', value: true },
		{ label: 'Draft', value: false },
		{ label: 'All', value: null },
	];

	const getLocations = async (params) => dispatch(projectLocationActions.searchProjectLocations(params));

	const loadProjectLocationListFilters = async (projectKey) => dispatch(projectLocationActions.loadProjectLocationListFilters(projectKey));

	const getProjectManagers = async (params) => dispatch(projectManagerActions.searchProjectManagers(params));

	const removeFromScope = async (params) => dispatch(projectLocationActions.removeFromScope(params));

	const getBaselines = async (subscriptionId, projectKey) => dispatch(projectLocationActions.loadPushdownFilters(subscriptionId, projectKey));

	const postPushdown = async (params) => dispatch(projectLocationActions.pushdown(params));

	const revokePushdown = async (locationIds: Array<number>, projectKey: string) =>
		dispatch(projectLocationActions.revokePushdown(locationIds, projectKey));

	const postOnHold = async (locationIds: Array<number>, projectKey: string, isOnHold: boolean) =>
		dispatch(projectLocationActions.updateOnHold(locationIds, projectKey, isOnHold));

	const publishModal = useRef();
	const infoWindow = useRef();

	const formatBaselines = async () => {
		const originalData = await getBaselines(subscriptionId, projectData.projectKey);

		const transformedData = originalData.payload.baselines.map((item) => ({
			name: item.name,
			code: item.id,
			id: item.id,
		}));

		return transformedData;
	};

	const formatProjectManagers = async () => {
		const originalData = await getProjectManagers({
			IsActive: true,
			PageSize: 0,
			SortBy: 'name',
		});

		setpProjectManagersForDrawer(originalData.payload.results);

		const transformedData = originalData.payload.results.map((item) => ({
			label: item.name,
			value: item.id,
		}));

		return transformedData;
	};

	const formatLoadProjectLocationListFilters = async () => {
		setIsLoadingFormFilters(true);
		const originalData = await loadProjectLocationListFilters(projectData.projectKey);
		const payload = originalData.payload;

		if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_SUCCESS) {
			setProjectLocationListFilters(payload);
			setIsLoadingFormFilters(false);
		} else if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_FAILURE) {
			Notification('error', 'An Error Occurred', payload.message);
			setIsLoadingFormFilters(false);
		}
	};

	const toggleAdvancedOptions = () => {
		setShowAdvancedOptions(!showAdvancedOptions);
	};

	const search = async (params: SearchParams) => {
		setIsLoading(true);

		try {
			const result = await getLocations(params);
			const payload = result.payload;
			if (result.type === types.SEARCH_PROJECTLOCATION_SUCCESS) {
				setSelectedBaselines([]);
				setSelectedLocations([]);
				setLocations(result.payload.results);
				setTotalCount(result.payload.totalCount);
				formatLoadProjectLocationListFilters();
			} else if (result.type === types.SEARCH_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const removeFromScopeClick = async (row) => {
		try {
			const param = {
				projectLocationIds: [row.id],
				projectKey: projectData.projectKey,
			};
			const result = await removeFromScope(param);
			const payload = result.payload;

			if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
				Notification('success', '', 'The location was removed from scope');
				search(searchParams);
			} else if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const openPublishModal = async (row) => {
		setSelectedLocations([row]);
		publishModal.current.show();
	};

	const onRevokePushdownIcon = async (row) => {
		try {
			setIsLoading(true);
			const response = await revokePushdown(
				[row].map((m) => m.id),
				projectData.projectKey
			);
			if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowRevokePushdownWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Revoke Pushdown was successful');
					search(searchParams);
				}
			} else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsLoading(false);
		}
	};

	const openInfoWindow = async () => {
		infoWindow.current.show();
	};

	const closeInfoWindow = async () => {
		infoWindow.current.hide();
	};

	const UpdateOnHoldClick = async (row) => {
		try {
			setIsLoading(true);

			const result = await postOnHold([row.id], projectData.projectKey, !row.isOnHold);
			const payload = result.payload;

			if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
				const notificationMessage = row.isOnHold ? 'activated' : 'archived';

				Notification('success', '', `The Project Location was successfully ${notificationMessage}`);
				search(searchParams);
				publishModal.current.hide();
			} else if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onViewChange = async (e: string) => {
		setSelectedBulkOperation(e);
	};

	const onRemoveFromScope = async () => {
		try {
			setIsLoading(true);
			var request = {
				subscriptionId: subscriptionId,
				projectKey: projectData.projectKey,
				projectLocationIds: selectedLocations.map((x) => x.id),
			};
			const response = await removeFromScope(request);
			if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
				if (response.payload.skippedPushedLocations) {
					setShowRemoveScopeWarning(true);
				}
				if (response.payload.removedSomeLocations) {
					Notification('success', 'Success', 'Remove from Scope completed successfully');
					search(searchParams);
				}
			} else if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onChangeManagerIcon = async (row) => {
		setSelectedLocations([row]);
		setIsChangeProjectManagerDrawerOpen(true);
	};

	const onChangeZeroDateIcon = (row) => {
		setSelectedLocations([row]);
		setIsChangeZeroDateDrawerOpen(true);
	};

	const onsAddBaselineDrawerIcon = (row) => {
		setSelectedLocations([row]);
		setIsAddBaselineDrawerOpen(true);
	};

	const onChangeManager = async () => {
		setIsChangeProjectManagerDrawerOpen(true);
	};

	const onChangeZeroDate = () => {
		setIsChangeZeroDateDrawerOpen(true);
	};

	const onRevokePushdown = async () => {
		try {
			setIsLoading(true);
			const response = await revokePushdown(
				selectedLocations.map((m) => m.id),
				projectData.projectKey
			);
			if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowRevokePushdownWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Revoke Pushdown was successful');
					search(searchParams);
				}
			} else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsLoading(false);
		}
	};

	const offHold = () => {
		updateOnHold(false);
	};

	const onHold = () => {
		updateOnHold(true);
	};

	const updateOnHold = async (isOnHold: boolean) => {
		setIsHoldOperation(isOnHold);
		try {
			const response = await postOnHold(
				selectedLocations.map((m) => m.id),
				projectData.projectKey,
				isOnHold
			);
			if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					setShowOnholdWarning(true);
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Locations updated successfully');
					search(searchParams);
				}
			} else if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	const selectBulkOperation = async () => {
		switch (selectedBulkOperation) {
			case BulkOptions.BulkUpdate.value:
				break;
			case BulkOptions.AddBaseline.value:
				setIsAddBaselineDrawerOpen(true);
				break;
			case BulkOptions.Publish.value:
				setSelectedBaselines([]);
				publishModal.current.show();
				break;
			case BulkOptions.RemoveScope.value:
				onRemoveFromScope();
				break;
			case BulkOptions.ChangeManager.value:
				onChangeManager();
				break;
			case BulkOptions.ChangeZeroDate.value:
				onChangeZeroDate();
				break;
			case BulkOptions.Unpublish.value:
				onRevokePushdown();
				break;
			case BulkOptions.OnHold.value:
				onHold();
				break;
			case BulkOptions.OffHold.value:
				offHold();
				break;
			default:
				break;
		}
	};

	const headerComponent = () => {
		return (
			<div className='header-buttons'>
				<SelectAllCheck>
					<SelectAllCheckbox />
					<span>Select All</span>
				</SelectAllCheck>
				<MultiSelectBox
					name='views'
					idField='id'
					valueField='name'
					placeholder='Select Bulk Action'
					className={'multi-select-bulk'}
					options={availableBulkOperations}
					onChange={onViewChange}
					selectedValues={selectedBulkOperation}
					isMulti={false}
					isSearchable={false}
				/>
				<Button label='Apply' onClick={selectBulkOperation} disabled={selectedLocations.length === 0} className='bulk-operation-btn' />
			</div>
		);
	};

	const statusBodyTemplate = (rowData) => {
		let description = 'Draft';
		let color = 'icon-orange';

		if (rowData.isPushedDown === true && rowData.isOnHold === false) {
			description = 'Published';
			color = 'icon-green';
		}

		if (rowData.isOnHold === true) {
			description = 'On Hold';
			color = 'icon-black';
		}

		return (
			<div className='pacer-badge-container'>
				<span className='pacer-badge'>
					<i className={`pi pi-circle-on ${color}`}></i>
					<span className='pacer-status'>{description}</span>
				</span>
			</div>
		);
	};

	const zeroDateBodyTemplate = (rowData) => {
		let value = '-';

		if (rowData.zeroDate) {
			value = Moment.utc(rowData.zeroDate).format(DATE_FORMAT);
		}

		return <span>{value}</span>;
	};

	const baselineBodyTemplate = (rowData: ProjectLocation) => {
		if (rowData.baselines.length === 0) {
			return <span>-</span>;
		}

		return rowData.baselines.map((item) => <div key={item.id}>{item.name}</div>);
	};

	const actionBodyTemplate = (rowData) => {
		if (rowData.isPushedDown === true && rowData.isOnHold === false) {
			return (
				<>
					<Button
						icon='fa-regular fa-calendar-lines-pen'
						onClick={() => onChangeZeroDateIcon(rowData)}
						title='Change Zero Date'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-square-list'
						onClick={() => onsAddBaselineDrawerIcon(rowData)}
						title='Add Baselines'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-user-pen'
						onClick={() => onChangeManagerIcon(rowData)}
						title='Change Project Manager'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-circle-pause'
						onClick={() => UpdateOnHoldClick(rowData)}
						title='Pause Project'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
					<Button
						icon='fa-regular fa-arrows-rotate'
						onClick={() => onRevokePushdownIcon(rowData)}
						title='Revoke Project'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
				</>
			);
		}

		if (rowData.isPushedDown === true && rowData.isOnHold === true) {
			return (
				<>
					<Button
						icon='fa-regular fa-circle-play'
						onClick={() => UpdateOnHoldClick(rowData)}
						title='Unpause Project'
						className='p-button-rounded p-button-outlined p-button-text p-button-operations fa-lg mx-2'
					/>
				</>
			);
		}

		return (
			<>
				<Button
					icon='fa-regular fa-paper-plane-top'
					onClick={() => openPublishModal(rowData)}
					title='Publish'
					className='p-button-rounded p-button-text p-button-operations fa-lg mx-2'
				/>
				<Button
					icon='fa-regular fa-trash-can'
					onClick={() => removeFromScopeClick(rowData)}
					title='Remove from Scope'
					className='fa-lg mx-2'
					style={{ color: '#ef4444' }}
				/>
			</>
		);
	};

	const onPageChange = async (e) => {
		const updatedParams = {
			...searchParams,
			pageNumber: e.page + 1,
			pageSize: e.rows,
		};

		setSearchParams(updatedParams);
		await search(updatedParams);
	};

	// Function to handle "Select All" checkbox click
	const toggleSelectAll = useCallback(() => {
		if (selectedLocations.length === locations.length && locations.length > 0) {
			setSelectedLocations([]);
		} else {
			setSelectedLocations([...locations]);
		}
	}, [locations, selectedLocations]);

	// Handler for DataTable selection changes
	const onSelectionChange = useCallback((e) => {
		setSelectedLocations(e.value);
	}, []);

	// Function to handle individual row checkbox click
	const toggleRowSelection = (row) => {
		if (selectedLocations.some((location) => location.id === row.id)) {
			// Remove the row based on its id
			setSelectedLocations(selectedLocations.filter((location) => location.id !== row.id));
		} else {
			// Add the row to selected locations
			setSelectedLocations([...selectedLocations, row]);
		}
	};

	const onMultiSelectChange = async (name, value) => {
		// Create a copy of the current searchParams
		const updatedParams = { ...searchParams };

		// Update the copy with the new property name and value
		updatedParams[name] = value;

		setSearchParams(updatedParams);
	};

	const buildSearchRequest = (): SearchParams => {
		let projectManagerIds: number[] = [];
		if (searchParams.projectManagers.length > 0) {
			projectManagerIds = searchParams.projectManagers.map((obj) => obj.id);
		}

		let baselineIds: number[] = [];
		if (searchParams.baselines.length > 0) {
			baselineIds = searchParams.baselines.map((obj) => obj.id);
		}

		let zeroDates: string[] = [];
		if (searchParams.projectZeroDates.length > 0) {
			zeroDates = searchParams.projectZeroDates.map((obj) => obj.name);
		}

		return {
			...searchParams,
			projectManagerIds: projectManagerIds,
			baselineIds: baselineIds,
			zeroDates: zeroDates,
			locationName: projectLocationListFilters.locationName,
			locationAbbreviations: abbreviationsToArray(projectLocationListFilters.locationAbbreviations),
		};
	};

	const abbreviationsToArray = (abbreviations) => {
		if (!abbreviations) {
			return [];
		}

		if (abbreviations.includes(',')) {
			return abbreviations.split(',');
		}

		return abbreviations.split(' ');
	};

	const filterChange = (e: { target: { name: string; value: string } }) => {
		let name = e.target.name;
		const updatedParams = { ...projectLocationListFilters };
		updatedParams[name] = e.target.value;
		setProjectLocationListFilters(updatedParams);
	};

	const keyUpFilter = (e: { keyCode: number }) => {
		const triggerKeys = [13, 9];

		if (triggerKeys.includes(e.keyCode)) {
			const params = buildSearchRequest();
			search(params);
		}
	};

	const onPublishModal = async () => {
		if (selectedBaselines.length === 0) {
			Notification('error', 'Validation Error', 'At least one Baseline is required');
			return;
		}

		if (!zeroDate) {
			Notification('error', 'Validation Error', 'Zero Date is required');
			return;
		}

		try {
			const locationIds = selectedLocations.map((location) => location.locationId).filter((id): id is number => id !== undefined);
			setIsPublishing((prev) => new Set([...prev, ...locationIds]));

			const param = {
				projectManagerId: selectedProjectOwner,
				projectKey: projectData.projectKey,
				zeroDate: Moment.utc(zeroDate).format(DATE_FORMAT),
				BaselineIds: selectedBaselines.map((obj) => obj.code),
				LocationIds: locationIds,
				sendEmail: true,
				daysSelected: applicableProjectDays,
			};

			const result = await postPushdown(param);
			const payload = result.payload;

			if (result.type === types.PUSHDOWN_PROJECTLOCATION_SUCCESS) {
				publishModal.current.hide();
				Notification(
					'info',
					'Publishing Project',
					'We are publishing your project. This may take several minutes. You can move on to your next tasks in the meantime and we will email you when the project has published to all teams.'
				);
			} else if (result.type === types.PUSHDOWN_PROJECTLOCATION_FAILURE) {
				setIsPublishing((prev) => {
					const updated = new Set(prev);
					locationIds.forEach((id) => updated.delete(id));
					return updated;
				});
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			setIsPublishing((prev) => {
				const updated = new Set(prev);
				selectedLocations
					.map((location) => location.locationId)
					.filter((id): id is number => id !== undefined)
					.forEach((id) => updated.delete(id));
				return updated;
			});
			Notification('error', 'Error', error);
		}
	};

	const onAddScopeCancel = () => {
		setIsAddToScopeDrawerOpen(false);
	};

	const isPublishFormValid = selectedBaselines.length > 0 && !!zeroDate;

	const onAddScopeComplete = () => {
		setIsAddToScopeDrawerOpen(false);
		search(searchParams);
	};

	const onChangeManagerComplete = () => {
		setIsChangeProjectManagerDrawerOpen(false);
		search(searchParams);
	};

	const onChangeManagerCancel = () => {
		setIsChangeProjectManagerDrawerOpen(false);
	};

	const onAddBaselineCancel = () => {
		setIsAddBaselineDrawerOpen(false);
	};

	const onAddBaselineComplete = () => {
		setIsAddBaselineDrawerOpen(false);
		search(searchParams);
	};

	const onChangeZeroDateComplete = (showZeroDateWarning: boolean) => {
		setIsChangeZeroDateDrawerOpen(false);
		search(searchParams);
	};
	const onChangeZeroDateCancel = () => {
		setIsChangeZeroDateDrawerOpen(false);
	};

	const closeRemoveScopeWarning = () => {
		setShowRemoveScopeWarning(false);
	};

	const closeRevokePushdownWarning = () => {
		setShowRevokePushdownWarning(false);
	};

	const closeOnholdWarning = () => {
		setShowOnholdWarning(false);
	};

	const getAdditionalBaselines = (): Array<MultiSelectOption> => {
		//returning an array of Baselines that are not already pushed down to any of the selected locations
		//comparison is done by Baseline name: app does not allow duplicate baseline names in the same subscription
		const baselineNames = new Set(selectedLocations.flatMap((location) => location.baselines).map((baseline) => baseline.name));

		return projectBaselines.filter(({ name }) => !baselineNames.has(name));
	};

	const scopeWarningModalBody =
		'Some locations were not removed from scope because the project has been published to them. Before removing locations from scope, consider filtering the Status column to a value of Published.';
	const scopeWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRemoveScopeWarning}>
				Ok
			</button>
		</div>
	);

	const revokePushdownWarningModalBody =
		'Some locations/teams were not updated because the project has not been published to them. Before unpublishing, consider filtering the Status column to a value of Published.';
	const revokePushdownWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRevokePushdownWarning}>
				Ok
			</button>
		</div>
	);

	const onholdWarningModalBody = `Some locations/teams were not updated because the project has not been published to them. Before selecting ${isHoldOperation ? 'On Hold' : 'Off Hold'
		}, consider filtering the Status column to a value of Published.`;

	const onholdWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeOnholdWarning}>
				Ok
			</button>
		</div>
	);

	useEffect(() => {
		const fetchData = async () => {
			setProjectBaselines(await formatBaselines());
			setProjectManagers(await formatProjectManagers());
			formatLoadProjectLocationListFilters();
			setSelectedBulkOperation(BulkOptions.Publish.value);
		};

		fetchData();

		const params = buildSearchRequest();
		search(params);
	}, [searchParams]);

	useEffect(() => {
		const specificOptionsToToggle = [
			{ id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label },
			{ id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
			{ id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
		];

		const draftCount = selectedLocations.filter((row) => row.isPushedDown == false).length;

		if (draftCount === 0) {
			// Add specific options if not already present
			setAvailableBulkOperations((prevOptions) => {
				const newOptions = specificOptionsToToggle.filter((option) => !prevOptions.some((existingOption) => existingOption.id === option.id));
				return [...prevOptions, ...newOptions];
			});
		} else {
			// Remove specific options
			setAvailableBulkOperations((prevOptions) =>
				prevOptions.filter((option) => !specificOptionsToToggle.some((specificOption) => specificOption.id === option.id))
			);
		}
	}, [selectedLocations]);

	useEffect(() => {
		// Join a group based on subscription
		signalRService.joinGroup(projectData.projectKey);

		// Initialize the SignalR service and set up global event listeners
		signalRService.on('UpdateProjectLocationStatus', (projectLocation) => {
			setIsPublishing((prev) => {
				const updated = new Set(prev);
				updated.delete(projectLocation.locationId);
				return updated;
			});

			// Update the specific record in the locations state
			setLocations((prevLocations) => {
				// Find the index of the location with the matching ID
				const locationIndex = prevLocations.findIndex((loc) => loc.id === projectLocation.id);

				if (locationIndex !== -1) {
					// Replace the existing record with the new one
					const updatedLocations = [...prevLocations];
					updatedLocations[locationIndex] = projectLocation;
					return updatedLocations;
				}

				// If no matching record is found, return the existing locations
				return prevLocations;
			});
		});
	}, []);

	return (
		<div className='publish-content'>
			<div className='wizard-content-title clearfix row'>
				<div className='col-sm-6'>
					<span>Publish the project to your teams. You can use the bulk option if the Zero Date and tasks are the same.</span>
				</div>
				<div className='col-sm-6 pacer-justify-content-end'>
					<Button icon='pi pi-info-circle' label='What is a Zero Date?' className='p-button-link me-3' onClick={openInfoWindow} />
					<Button icon='pi pi-info-circle' label='What is a Published Project?' className='p-button-link' onClick={openInfoWindow} />
				</div>
			</div>

			<DataTable
				filterDisplay='row'
				value={locations}
				header={headerComponent()}
				className='p-datatable-sm p-datatable-striped'
				sortField='locationName'
				sortOrder={1}
				selectionMode="checkbox"
				selection={selectedLocations}
				onSelectionChange={onSelectionChange}
				dataKey="id"
				showSelectAll={false}>
				<Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
				<Column
					field='locationName'
					header='Name'
					filter={true}
					sortable={true}
					style={{ width: '300px' }}
					filterElement={
						<input
							type='text'
							name='locationName'
							className='p-inputtext'
							placeholder='Filter by Name'
							onChange={filterChange}
							onKeyDown={keyUpFilter}
							value={projectLocationListFilters.locationName || ''}
						/>
					}
				/>
				<Column
					field='locationAbbreviation'
					header='Abbreviation'
					sortable={true}
					filter={true}
					style={{ width: '100px' }}
					filterElement={
						<input
							type='text'
							name='locationAbbreviations'
							className='p-inputtext'
							placeholder='Filter by Abbreviation'
							onKeyDown={keyUpFilter}
							onChange={filterChange}
							value={projectLocationListFilters.locationAbbreviations || ''}
						/>
					}
				/>
				<Column
					body={statusBodyTemplate}
					header='Status'
					style={{ width: '110px' }}
					sortable={true}
					filter={true}
					filterElement={
						<Dropdown
							name='isPushedDown'
							className='ui-column-filter w-100'
							value={searchParams.isPushedDown}
							options={activeOptions}
							onChange={(e) => onMultiSelectChange('isPushedDown', e.value)}
						/>
					}
				/>
				<Column
					body={zeroDateBodyTemplate}
					field='zeroDates'
					header='Zero Date'
					style={{ width: '100px' }}
					filter={true}
					sortable={true}
					filterElement={
						<MultiSelect
							className='ui-column-filter'
							optionLabel='name'
							placeholder='Select'
							value={searchParams.projectZeroDates}
							options={projectLocationListFilters.projectZeroDates}
							onChange={(e) => onMultiSelectChange('projectZeroDates', e.value)}
						/>
					}
				/>
				<Column
					body={baselineBodyTemplate}
					field='baselines'
					header='Baselines Assigned'
					filter={true}
					filterElement={
						<MultiSelect
							className='ui-column-filter'
							optionLabel='name'
							placeholder='Select'
							value={searchParams.baselines}
							options={projectLocationListFilters.baselines}
							onChange={(e) => onMultiSelectChange('baselines', e.value)}
						/>
					}
				/>
				<Column
					field='projectManager'
					header='Implementation Manager'
					filter={true}
					filterElement={
						<MultiSelect
							className='ui-column-filter'
							optionLabel='name'
							placeholder='Select'
							value={searchParams.projectManagers}
							options={projectLocationListFilters.projectManagers}
							onChange={(e) => onMultiSelectChange('projectManagers', e.value)}
						/>
					}
				/>
				<Column field='actions' body={actionBodyTemplate} style={{ width: '260px', textAlign: 'right' }} />
			</DataTable>

			<Paginator
				leftContent={<span>Rows per page: {searchParams.pageSize}</span>}
				rows={searchParams.pageSize}
				totalRecords={totalCount}
				first={(searchParams.pageNumber - 1) * searchParams.pageSize}
				rowsPerPageOptions={[20, 50, 100]}
				onPageChange={onPageChange}
				template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
				currentPageReportTemplate='{first}-{last} of {totalRecords}'
			/>

			{isAddToScopeDrawerOpen && (
				<AddToScopeDrawer
					subscriptionId={subscriptionId}
					projectKey={projectData.projectKey}
					onCancel={onAddScopeCancel}
					onComplete={onAddScopeComplete}
					projectLocationActions={projectLocationActions}
				/>
			)}
			{isChangeProjectManagerDrawerOpen && (
				<ChangeProjectManagerDrawer
					projectManagers={projectManagersForDrawer}
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onChangeManagerCancel}
					onComplete={onChangeManagerComplete}
					isLoading={isLoadingFormFilters}
				/>
			)}
			{isAddBaselineDrawerOpen && (
				<AddBaselineDrawer
					baselines={getAdditionalBaselines()}
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onAddBaselineCancel}
					onComplete={onAddBaselineComplete}
					isLoading={isLoadingFormFilters}
				/>
			)}
			{isChangeZeroDateDrawerOpen && (
				<ChangeZeroDateDrawer
					selectedProjectLocations={selectedLocations}
					projectKey={projectData.projectKey}
					onCancel={onChangeZeroDateCancel}
					onComplete={onChangeZeroDateComplete}
				/>
			)}

			{showRemoveScopeWarning && (
				<Modal
					id='pushedDown'
					title='Skipped Locations'
					body={scopeWarningModalBody}
					onCloseClick={closeRemoveScopeWarning}
					customFooter={scopeWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}
			{showRevokePushdownWarning && (
				<Modal
					id='revokePushdownWarning'
					title='Skipped Locations'
					body={revokePushdownWarningModalBody}
					onCloseClick={closeRevokePushdownWarning}
					customFooter={revokePushdownWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}
			{showOnholdWarning && (
				<Modal
					id='onholdWarning'
					title='Skipped Locations'
					body={onholdWarningModalBody}
					onCloseClick={closeOnholdWarning}
					customFooter={onholdWarningModalFooter}
					style={{ top: '30%' }}
				/>
			)}

			<Modal61
				ref={publishModal}
				title='Publish Project'
				body={
					<>
						<div className='row'>
							<div className='col-sm-6'>
								<div className='mb-1'>
									<label>Baseline(s):</label>
								</div>
								<MultiSelect
									optionLabel='name'
									required={true}
									className='w-100 pacer-multiselect'
									value={selectedBaselines}
									options={projectBaselines}
									display='chip'
									onChange={(e) => {
										setSelectedBaselines(e.value);
									}}
								/>
							</div>
							<div className='col-sm-6'>
								<div className='mb-1'>
									<label>Zero Date:</label>
								</div>
								<Calendar
									value={zeroDate}
									className='pacer-calendar'
									onChange={(e) => setZeroDate(e.value ?? null)}
									placeholder='When is this project complete?'
									dateFormat='dd M yy'
									showIcon
									iconPos='left'
									required={true}
								/>
							</div>
						</div>
						<div className='row'>
							<div className='text-end col-sm-12'>
								<button className='btn btn-link mt-3 mb-3' onClick={toggleAdvancedOptions}>
									{showAdvancedOptions ? (
										<>
											<i className='pi pi-minus'></i> Hide Advanced Options
										</>
									) : (
										<>
											<i className='pi pi-plus'></i> Show Advanced Options
										</>
									)}
								</button>
							</div>
						</div>

						{showAdvancedOptions && (
							<>
								<div className='row'>
									<div className='col-sm-6'>
										<div className='mb-1'>
											<label>Project Owner:</label>
										</div>
										<Dropdown
											value={selectedProjectOwner}
											options={projectManagers}
											className='w-100'
											onChange={(e) => setSelectedProjectOwner(e.value)}
											placeholder='Select Project Owner'
										/>
									</div>
								</div>
							</>
						)}
					</>
				}
				customFooter={
					<>
						<button
							type='button'
							className='btn btn-primary'
							data-dismiss='modal'
							onClick={onPublishModal}
							disabled={
								!isPublishFormValid ||
								selectedLocations
									.map((loc) => loc.locationId)
									.filter((id): id is number => id !== undefined)
									.some((id) => isPublishing.has(id))
							}>
							Publish
						</button>
					</>
				}
				primaryButtonText='Publish'
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={undefined}
				isLoading={false}
				isCentered={false}
			/>

			<Modal61
				ref={infoWindow}
				body={
					<InfoWindowBody>
						<div className='row justify-content-center'>
							<InfoWindowIcon>
								<i className='fa-regular fa-paper-plane'></i>
							</InfoWindowIcon>
						</div>
						<div className='row justify-content-center'>
							<InfoWindowIconDescription>
								Almost Complete!
								<br />
								Ready to Publish?
							</InfoWindowIconDescription>
						</div>

						<InfoWindowTopicTitle>What is a Project Zero Date?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							In order to publish a project to a location, you will need to set a Project Zero Date. This is the ‘go live’ or ‘launch
							date’ of a selected location.
						</InfoWindowTopicBody>

						<InfoWindowTopicTitle>What does publishing a project mean?</InfoWindowTopicTitle>
						<InfoWindowTopicBody>
							Publishing a project will allow the selected location(s) to see this project within their Dashboard. Prior to publication,
							the project will live in a draft state. You can always make changes post publication.
						</InfoWindowTopicBody>
					</InfoWindowBody>
				}
				customFooter={
					<>
						<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeInfoWindow}>
							Go to Publish Page
						</button>
					</>
				}
				title={''}
				primaryButtonText={''}
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={closeInfoWindow}
				isLoading={false}
				isCentered={true}
			/>
		</div>
	);
};

const mapDispatchToProps = (dispatch) => {
	return {
		projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
	};
};

export default connect(mapDispatchToProps)(Publish);
